<template>
	<div class="flex-grow flex flex-col py-5 px-8">
		<!-- STEP 1: Person -->
		<template v-if="step === 1">
			<Header :title="$t('customer').toString()">
				<p>
					{{ $t('required_data_red_stars') }}
				</p>
			</Header>

			<div class="main">
				<DamageAssessorCaseCustomerForm ref="CustomerForm" v-model="person" />
			</div>

			<Footer
				@backButtonClick="goBack"
				@continueButtonClick="continuePerson"
				:is-back-button="true"
				:is-continue-button="true"
				:is-skippable="false"
				class="m-8"
			/>
		</template>

		<!-- STEP 2: Vehicle -->
		<template v-if="step === 2">
			<Header :title="$t('vehicle').toString()">
				<p>
					Fahrzeugdaten können optional angegeben werden.
				</p>
			</Header>

			<div class="main">
				<CustomerVehicleForm
					ref="VehicleForm"
					v-model="vehicle"
					:display="true"
					validation-mode="damageAssessorCase"
					@updateValue="vehicle = $event"
				/>
			</div>

			<Footer
				@backButtonClick="goToStep(1)"
				@continueButtonClick="continueVehicle(false)"
				@skipButtonClick="continueVehicle(true)"
				:is-back-button="true"
				:is-continue-button="true"
				:is-skippable="true"
				class="m-8"
			/>
		</template>

		<!-- STEP 3: Accident -->
		<template v-if="step === 3">
			<Header :title="$t('accident_data').toString()">
				{{ $t('create_document_information') }}
			</Header>

			<div class="main">
				<DamageAssessorCaseAccidentDataForm ref="AccidentDataForm" v-model="accident" />
			</div>

			<Footer
				@backButtonClick="goToStep(2)"
				@continueButtonClick="continueAccident(false)"
				@skipButtonClick="continueAccident(true)"
				:is-back-button="true"
				:is-continue-button="true"
				:is-skippable="true"
				class="m-8"
			/>
		</template>

		<!-- STEP 4: Opponent -->
		<template v-if="step === 4">
			<Header :title="$t('new_damage_case_opponent').toString()">
				<p>
					{{ $t('required_data_red_stars') }}
				</p>
			</Header>

			<div class="main">
				<DamageAssessorCaseOppositePersonForm ref="OppositePersonForm" v-model="oppositeParty" />
			</div>

			<Footer
				@backButtonClick="goToStep(3)"
				@skipButtonClick="skipOppositeParty"
				:is-back-button="true"
				:is-continue-button="false"
				:is-skippable="true"
				class="m-8"
			>
				<template #right>
					<BaseButton @click="saveOppositeParty" :disabled="isLoading" class="btn-blue">
						{{ $t('general.save') }}
					</BaseButton>
				</template>
			</Footer>
		</template>

		<LoadingSpinner v-if="isLoading" />
	</div>
</template>

<script>
import Header from '@/components/damagecases/Header';
import Footer from '@/components/damagecases/Footer';

import DamageAssessorCaseCustomerForm from '@/components/damageassessorcases/forms/DamageAssessorCaseCustomerForm';
import DamageAssessorCaseAccidentDataForm from '@/components/damageassessorcases/forms/DamageAssessorCaseAccidentDataForm';
import DamageAssessorCaseOppositePersonForm from '@/components/damageassessorcases/forms/DamageAssessorCaseOppositePersonForm';

import CustomerVehicleForm from '@/components/forms/CustomerVehicleForm';

import BaseButton from '@/components/forms/buttons/BaseButton';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

export default {
	name: 'DamageAssessorCaseCreate',

	components: {
		Header,
		Footer,

		DamageAssessorCaseCustomerForm,
		DamageAssessorCaseAccidentDataForm,
		DamageAssessorCaseOppositePersonForm,

		CustomerVehicleForm,
		BaseButton,
		LoadingSpinner,
	},

	data() {
		return {
			step: 1,

			isLoading: false,

			/*
			 * Step 1: Customer
			 */
			person: {
				city: {
					id: null,
					zipCode: null,
					name: null,
				},
			},

			/*
			 * Step 2: Vehicle
			 */
			vehicle: {
				licensePlate: {
					text: null,
					isSpecial: false,
				},

				vehicleIdentificationNumber: '',
			},

			vehicleSkipped: false,

			/*
			 * Step 3: Accident
			 */
			accident: {
				date: null,

				damageTimeSetByUser: false,

				accidentLocation: null,

				isRecordedByPolice: null,

				isPersonInjured: null,

				accidentReport: null,
			},

			accidentSkipped: false,

			/*
			 * Step 4: Opponent
			 */
			oppositeParty: {
				oppositePerson: null,

				oppositeLicensePlate: {
					text: null,
					isSpecial: false,
				},

				insuranceName: null,

				insuranceNumber: null,
			},
		};
	},

	mounted() {
		this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
	},

	destroyed() {
		this.$store.commit('setConfirmLeaving', false);
	},

	methods: {
		goBack() {
			this.$router.go(-1);
		},

		goToStep(step) {
			this.step = step;
		},

		continuePerson() {
			if (!this.$refs.CustomerForm.validate()) {
				this.showValidationError();

				return;
			}

			this.step = 2;
		},

		continueVehicle(skip) {
			if (skip) {
				this.vehicleSkipped = true;

				this.step = 3;

				return;
			}

			if (!this.$refs.VehicleForm.validate()) {
				this.showValidationError();

				return;
			}

			this.vehicleSkipped = false;

			this.step = 3;
		},

		continueAccident(skip) {
			if (skip) {
				this.accidentSkipped = true;
				this.accident = {
					date: null,
					damageTimeSetByUser: false,
					accidentLocation: null,
					isRecordedByPolice: null,
					isPersonInjured: null,
					accidentReport: null,
				};
				this.step = 4;
				return;
			}
			if (this.$refs.AccidentDataForm && !this.$refs.AccidentDataForm.validate()) {
				this.showValidationError();
				return;
			}
			this.accidentSkipped = false;
			this.step = 4;
		},

		saveOppositeParty() {
			if (this.$refs.OppositePersonForm && !this.$refs.OppositePersonForm.validate()) {
				this.showValidationError();

				return;
			}

			this.save();
		},

		skipOppositeParty() {
			this.oppositeParty = {
				oppositePerson: null,

				oppositeLicensePlate: {
					text: null,

					isSpecial: false,
				},

				insuranceName: null,

				insuranceNumber: null,
			};

			this.save();
		},

		buildDto() {
			const dto = {
				damage_assessor_case: this.buildDamageAssessorCaseDto(),

				person: this.buildPersonDto(),
			};

			const vehicle = this.buildVehicleDto();

			if (vehicle) {
				dto.vehicle = vehicle;
			}

			const oppositePerson = this.buildOppositePersonDto();

			if (oppositePerson) {
				dto.opposite_person = oppositePerson;
			}

			const accidentReport = this.buildAccidentReportDto();

			if (accidentReport) {
				dto.accident_report = accidentReport;
			}

			return dto;
		},

		buildDamageAssessorCaseDto() {
			return {
				causer_license_plate: this.oppositeParty?.oppositeLicensePlate?.text ?? null,

				is_causer_license_plate_special: this.oppositeParty?.oppositeLicensePlate?.isSpecial ?? false,

				opposite_insurance_name: this.oppositeParty?.insuranceName ?? null,

				opposite_insurance_number: this.oppositeParty?.insuranceNumber ?? null,

				date_of_accident: this.accidentSkipped ? null : this.accident?.date ?? null,

				damage_time_set_by_user: this.accidentSkipped ? false : this.accident?.damageTimeSetByUser ?? false,

				accident_location: this.accidentSkipped ? null : this.accident?.accidentLocation ?? null,

				is_recorded_by_police: this.accidentSkipped ? null : this.accident?.isRecordedByPolice ?? null,

				is_person_injured: this.accidentSkipped ? null : this.accident?.isPersonInjured ?? null,
			};
		},

		buildPersonDto() {
			return this.buildPersonPayload(this.person);
		},

		buildOppositePersonDto() {
			const person = this.oppositeParty?.oppositePerson;

			const firstName = person?.firstName?.trim() || null;
			const lastName = person?.lastName?.trim() || null;

			if (!firstName && !lastName) {
				return null;
			}

			return {
				firstName,
				lastName,
			};
		},

		buildPersonPayload(person) {
			if (!person) {
				return null;
			}

			return {
				title: person.title ?? null,

				gender: person.gender ?? null,

				firstName: person.firstName ?? null,

				lastName: person.lastName ?? null,

				street: person.street ?? null,

				houseNumber: person.houseNumber ?? null,

				poBox: person.poBox ?? null,

				city: person.city
					? {
							id: person.city.id ?? null,

							zipCode: person.city.zipCode ?? null,

							name: person.city.name ?? null,
					  }
					: null,

				phoneNumber: person.phoneNumber ?? null,

				cellNumber: person.cellNumber ?? null,

				email: person.email ?? null,

				organisation: person.organisation ?? null,
			};
		},

		buildVehicleDto() {
			if (this.vehicleSkipped) {
				return null;
			}

			return {
				...this.vehicle,
			};
		},

		buildAccidentReportDto() {
			if (this.accidentSkipped) {
				return null;
			}

			return this.accident?.accidentReport ?? null;
		},

		save() {
			if (this.isLoading) {
				return;
			}

			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.createDamageAssessorCase(this.buildDto())
				.then(response => {
					this.$store.commit('setConfirmLeaving', false);

					return this.$swal({
						title: this.$t('general.success').toString(),

						text: this.$t('general.changes_saved').toString(),

						icon: 'success',
					}).then(() => {
						this.$router.push({
							name: 'damageAssessorCaseDetail',

							params: {
								id: response.data.id,
							},
						});
					});
				})
				.catch(error => {
					console.log(error);

					this.$swal(
						this.$t('general.error').toString(),

						this.$t('please_check_input').toString(),

						'error',
					);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		showValidationError() {
			this.$swal(
				this.$t('general.attention').toString(),

				this.$t('please_check_input').toString(),

				'warning',
			);
		},
	},
};
</script>

<style scoped></style>
